import { userManager } from '@/config/keycloak';
import type { ModelSolid, StepDocumentModel, TaskProgress, UIDResponse } from '@/types/project.types';
import { apiClient } from './client';

// Get config from runtime window object (loaded from config.js) — same source as client.ts
const appConfig = (window as any).__APP_CONFIG__ || {};
const SSE_BASE_URL = appConfig.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

/** GET /3d/project/{project_id}/model-upload-task */
export interface ModelUploadTaskResponse {
  task_id: string | null;
}

/**
 * Preview route for one solid of a STEP document.
 *
 * Both solids live on the same `step_document` row, so they share `modelId` and are told apart
 * by the `/stock` suffix only — `model` is the bare route it has always been.
 */
const previewPath = (kind: 'png' | 'gif', modelId: string, solid: ModelSolid): string =>
  solid === 'stock' ? `/preview/${kind}/${modelId}/stock` : `/preview/${kind}/${modelId}`;

export const modelsApi = {
  // GET /3d/{model_id} - the project's one STEP document
  get: async (modelId: string): Promise<StepDocumentModel> => {
    const { data } = await apiClient.get<StepDocumentModel>(`/3d/${modelId}`);
    return data;
  },

  // DELETE /3d/{model_id}
  delete: async (modelId: string): Promise<void> => {
    await apiClient.delete(`/3d/${modelId}`);
  },

  // GET /3d/{model_id}/download - download original STP file from S3
  downloadSTP: async (modelId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/3d/${modelId}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /preview/png/{model_id}[/stock]
  getPngUrl: (modelId: string, solid: ModelSolid = 'model'): string => {
    return `${apiClient.defaults.baseURL}${previewPath('png', modelId, solid)}`;
  },

  // GET /preview/gif/{model_id}[/stock]
  getGifUrl: (modelId: string, solid: ModelSolid = 'model'): string => {
    return `${apiClient.defaults.baseURL}${previewPath('gif', modelId, solid)}`;
  },

  // GET /preview/png/{model_id}[/stock] - fetch as blob with auth
  fetchPngBlob: async (modelId: string, solid: ModelSolid = 'model'): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(previewPath('png', modelId, solid), {
      responseType: 'blob',
    });
    return data;
  },

  // GET /preview/gif/{model_id}[/stock] - fetch as blob with auth
  fetchGifBlob: async (modelId: string, solid: ModelSolid = 'model'): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(previewPath('gif', modelId, solid), {
      responseType: 'blob',
    });
    return data;
  },

  // POST /3d/embedded/{project_id}/background - upload the one STP that carries both the model
  // and its stock; the backend loads it into a single step_document row. This is the only
  // remaining model upload route — the per-solid target/blank uploads are gone.
  uploadEmbeddedBackground: async (projectId: string, file: File): Promise<UIDResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<UIDResponse>(`/3d/embedded/${projectId}/background`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return data;
  },

  // SSE progress tracking for background upload
  // Uses fetch() streaming so that Authorization header can be set (EventSource doesn't support custom headers)
  trackProgressSSE: async (
    taskId: string,
    onProgress: (progress: TaskProgress) => void,
    signal?: AbortSignal
  ): Promise<TaskProgress> => {
    // Retrieve token the same way as the Axios request interceptor in client.ts
    let token: string | undefined;
    try {
      const user = await userManager.getUser();
      token = user?.access_token;
    } catch (error) {
      console.warn('Failed to get user from userManager, trying storage fallback:', error);

      const authority = appConfig.VITE_AUTHORITY || import.meta.env.VITE_AUTHORITY;
      const clientId = appConfig.VITE_CLIENT_ID || import.meta.env.VITE_CLIENT_ID;
      const storageKey = `oidc.user:${authority}:${clientId}`;

      let oidcStorage = localStorage.getItem(storageKey);
      if (!oidcStorage) {
        oidcStorage = sessionStorage.getItem(storageKey);
      }

      if (oidcStorage) {
        try {
          const stored = JSON.parse(oidcStorage);
          token = stored?.access_token;
        } catch (parseError) {
          console.error('Failed to parse OIDC storage:', parseError);
        }
      }
    }

    const response = await fetch(`${SSE_BASE_URL}/3d/task/${taskId}/stream`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('SSE response has no body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastProgress: TaskProgress | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() ?? '';

        for (const block of blocks) {
          if (!block.trim()) continue;

          // Parse named SSE event block:
          // "event: <type>\ndata: <json>"
          let eventType = 'message';
          let dataLine = '';

          for (const line of block.split('\n')) {
            if (line.startsWith('event:')) {
              eventType = line.slice('event:'.length).trim();
            } else if (line.startsWith('data:')) {
              dataLine = line.slice('data:'.length).trim();
            }
          }

          if (!dataLine) continue;

          let parsed: any;
          try {
            parsed = JSON.parse(dataLine);
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError, dataLine);
            continue;
          }

          if (eventType === 'progress') {
            const progress: TaskProgress = {
              task_id: taskId,
              status: parsed.status,
              progress: parsed.progress ?? 0,
              current_step: parsed.current_step ?? '',
              created_at: parsed.created_at ?? new Date().toISOString(),
              updated_at: parsed.updated_at ?? new Date().toISOString(),
            };
            lastProgress = progress;
            onProgress(progress);
          } else if (eventType === 'completed') {
            const progress: TaskProgress = {
              task_id: taskId,
              status: parsed.status ?? 'completed',
              progress: parsed.progress ?? 100,
              current_step: parsed.current_step ?? '',
              created_at: parsed.created_at ?? new Date().toISOString(),
              updated_at: parsed.updated_at ?? new Date().toISOString(),
              result: parsed.result ?? undefined,
            };
            onProgress(progress);
            return progress;
          } else if (eventType === 'error') {
            throw new Error(parsed.error || 'Upload failed');
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Stream ended without a completed/error event
    if (lastProgress) {
      throw new Error('SSE stream ended unexpectedly before task completion');
    }
    throw new Error('SSE stream ended without any progress data');
  },

  // Cancel a background task
  cancelTask: async (taskId: string): Promise<void> => {
    await apiClient.delete(`/3d/task/${taskId}`);
  },

  // HTTP polling fallback for progress tracking
  // Use this if WebSocket is not available
  getTaskStatus: async (taskId: string): Promise<TaskProgress> => {
    const { data } = await apiClient.get<TaskProgress>(`/3d/task/${taskId}/status`);
    return data;
  },

  /** Active background model upload task for this project (from server Redis registry). */
  getProjectModelUploadTask: async (projectId: string): Promise<ModelUploadTaskResponse> => {
    const { data } = await apiClient.get<ModelUploadTaskResponse>(`/3d/project/${projectId}/model-upload-task`);
    return data;
  },
};
